import {
  ScreenSchema,
  type Screen,
  expandDefaultCells,
  type SurfaceRecipeId,
  type Action,
  type Posture,
  type Cell,
} from '@vjal/screen-ir'
import type { ComponentType } from 'react'

/** Author-facing screen definition. cells optional — expanded when omitted. */
export type ScreenDef = {
  app: string
  screen_id: string
  path: string
  params?: string[]
  heading: { text: string; level?: number }
  roles: Record<string, 'allow' | 'deny'>
  capability_refs?: string[]
  states?: Posture[]
  actions?: Action[]
  recipe: SurfaceRecipeId
  cells?: Cell[]
  collection?: { enabled: true; id: string }
  data?: Screen['data']
  chrome?: Screen['chrome']
  authority?: Screen['authority']
}

export type DefinedScreen<P = Record<string, unknown>> = {
  ir: Screen
  Component: ComponentType<P>
  routeProps: {
    path: string
    screen_id: string
    app: string
  }
}

/**
 * defineScreen — returns IR fragment + Component + routeProps.
 * NO side-effect global register at import time (collector takes explicit entries).
 */
export function defineScreen<P = Record<string, unknown>>(
  def: ScreenDef & { Component: ComponentType<P> },
): DefinedScreen<P> {
  const { Component, ...rest } = def
  const states = rest.states ?? (['ready'] as Posture[])
  const actions = rest.actions ?? []
  const cells =
    rest.cells && rest.cells.length > 0
      ? rest.cells
      : expandDefaultCells({
          roles: rest.roles,
          states,
          actions,
        })

  const ir = ScreenSchema.parse({
    ...rest,
    states,
    actions,
    cells,
  })

  return {
    ir,
    Component,
    routeProps: {
      path: ir.path,
      screen_id: ir.screen_id,
      app: ir.app,
    },
  }
}
